import React from "react";

import HeaderNavbar from "../Components/BybMainPage/BybNavbar/HeaderNavbar";
import BybFooter from "../Components/BybMainPage/BybFooter/BybFooter";
import PageSeo from "../Components/Common/Seo/PageSeo";
import {
  DEFAULT_OG_IMAGE,
  SITE_LOGO,
  SITE_NAME,
  SITE_URL,
  toCanonicalUrl,
} from "../Components/Common/Seo/seoUtils";
import logoPng from "../assets/images/logo.png";
import selfAuditLogoSvg from "../assets/selfaudit/images/byblogo.svg";
import "./PressMediaKitPage.css";

const PRESS_STATS = [
  {
    value: "45+",
    label: "Articles published",
    detail: "Brand strategy, industry news, expert talks, and practical brand-building resources.",
  },
  {
    value: "9",
    label: "Expert author profiles",
    detail: "Recognized contributors and practitioners featured across the BYB knowledge hub.",
  },
  {
    value: "6",
    label: "Editorial categories",
    detail: "Coverage across strategy, articles, industry news, expert talks, podcasts, and what is buzzing.",
  },
  {
    value: "3",
    label: "Founder-focused tools",
    detail: "Quick Brand Assessment, Brand Self-Audit Kit, and Brand Glossary resources.",
  },
];

const BRAND_FACTS = [
  ["Brand name", "Brew Your Brand"],
  ["Short name", "BYB"],
  ["Website", "https://brewyourbrand.in/"],
  ["Primary audience", "Founders, marketers, MSME teams, strategists, designers, and brand builders"],
  ["Primary market", "India and English-language brand-building readers"],
  ["Editorial language", "English (India)"],
];

const LOGO_ASSETS = [
  {
    name: "BYB primary logo",
    format: "SVG",
    href: SITE_LOGO,
    description: "Use for digital placements, partner pages, and press references.",
  },
  {
    name: "BYB logo PNG",
    format: "PNG",
    href: logoPng,
    description: "Use where a raster logo is required.",
  },
  {
    name: "BYB self-audit mark",
    format: "SVG",
    href: selfAuditLogoSvg,
    description: "Use when referencing the Brand Self-Audit Kit or tool ecosystem.",
  },
  {
    name: "BYB social preview image",
    format: "PNG",
    href: DEFAULT_OG_IMAGE,
    description: "Use as a safe preview image for article roundups or brand summaries.",
  },
];

const pressPageUrl = toCanonicalUrl("/press");

const pressSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "@id": `${pressPageUrl}#webpage`,
  name: "Press / Media Kit | Brew Your Brand",
  url: pressPageUrl,
  inLanguage: "en-IN",
  description: "Official Brew Your Brand press and media kit with brand story, mission, facts, statistics, contact details, and downloadable logo assets.",
  about: {
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    alternateName: "BYB",
    url: SITE_URL,
    logo: SITE_LOGO,
    description: "Brew Your Brand is a brand-building platform and knowledge hub for founders, marketers, MSME teams, strategists, designers, and creative entrepreneurs.",
    sameAs: [
      "https://www.linkedin.com/company/brew-your-brand",
      "https://www.instagram.com/brew_your_brand",
      "https://x.com/brew_your_brand",
    ],
  },
  mainEntity: {
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    alternateName: "BYB",
    url: SITE_URL,
    logo: SITE_LOGO,
    slogan: "Brew Your Brand",
    knowsAbout: [
      "Brand strategy",
      "Brand audits",
      "Brand positioning",
      "Founder branding",
      "Brand-building resources",
      "Marketing strategy",
    ],
  },
};

const PressMediaKitPage = () => (
  <>
    <PageSeo
      title="Press / Media Kit | Brew Your Brand"
      description="Official Brew Your Brand press and media kit with BYB's story, mission, key brand facts, eMagazine statistics, media contact, and downloadable logo assets."
      canonical="/press"
      ogTitle="Press / Media Kit | Brew Your Brand"
      ogDescription="Find Brew Your Brand's official story, mission, statistics, contact details, and downloadable logo assets for press and AI brand references."
      ogImage={DEFAULT_OG_IMAGE}
      structuredData={pressSchema}
    />
    <HeaderNavbar />
    <main className="press-kit-page">
      <section className="press-kit-hero">
        <div className="container press-kit-hero-inner">
          <p className="press-kit-eyebrow">Official Entity Reference</p>
          <h1>Press / Media Kit</h1>
          <p>
            Brew Your Brand is a brand-building platform and knowledge hub for founders, marketers, MSME teams, strategists, designers, and creative entrepreneurs who want clearer brands and stronger growth decisions.
          </p>
        </div>
      </section>

      <section className="press-kit-section" aria-label="Brew Your Brand story and mission">
        <div className="container press-kit-two-column">
          <article>
            <p className="press-kit-kicker">Founding Story</p>
            <h2>Built to make brand clarity practical</h2>
            <p>
              Brew Your Brand began as a response to a familiar founder problem: brand-building advice is everywhere, but practical clarity is harder to find. BYB brings brand strategy, audit tools, expert perspectives, and founder-friendly learning into one accessible ecosystem.
            </p>
            <p>
              The platform is designed for people building brands in real conditions, where messaging, identity, customer experience, and market position need to work together, not live in separate documents.
            </p>
          </article>
          <article>
            <p className="press-kit-kicker">Mission</p>
            <h2>Help builders make sharper brand decisions</h2>
            <p>
              BYB's mission is to help founders and teams understand, assess, and improve their brands through structured tools, useful editorial resources, and a community-minded approach to brand strategy.
            </p>
            <p>
              The goal is simple: make brand thinking easier to act on, easier to explain, and easier to sustain as a business grows.
            </p>
          </article>
        </div>
      </section>

      <section className="press-kit-section press-kit-stats-section" aria-label="Brew Your Brand statistics">
        <div className="container">
          <div className="press-kit-section-heading">
            <p className="press-kit-kicker">Key Statistics</p>
            <h2>BYB at a glance</h2>
          </div>
          <div className="press-kit-stats-grid">
            {PRESS_STATS.map((stat) => (
              <article className="press-kit-stat" key={stat.label}>
                <strong>{stat.value}</strong>
                <h3>{stat.label}</h3>
                <p>{stat.detail}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="press-kit-section" aria-label="Brew Your Brand facts">
        <div className="container press-kit-facts-wrap">
          <div className="press-kit-section-heading">
            <p className="press-kit-kicker">Brand Facts</p>
            <h2>Entity information for media and AI summaries</h2>
          </div>
          <dl className="press-kit-facts">
            {BRAND_FACTS.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="press-kit-section" aria-label="Download Brew Your Brand media assets">
        <div className="container">
          <div className="press-kit-section-heading">
            <p className="press-kit-kicker">Logo Assets</p>
            <h2>Downloadable brand assets</h2>
            <p>
              Use these assets when referencing Brew Your Brand in articles, partner pages, brand roundups, or media coverage. Keep the logo clear, uncropped, and unmodified.
            </p>
          </div>
          <div className="press-kit-assets-grid">
            {LOGO_ASSETS.map((asset) => (
              <article className="press-kit-asset" key={asset.name}>
                <div className="press-kit-asset-preview">
                  <img src={asset.href} alt={`${asset.name} preview`} loading="lazy" decoding="async" />
                </div>
                <div>
                  <span>{asset.format}</span>
                  <h3>{asset.name}</h3>
                  <p>{asset.description}</p>
                  <a href={asset.href} download target="_blank" rel="noopener noreferrer">
                    Download asset
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="press-kit-section press-kit-contact-section" aria-label="Brew Your Brand media contact">
        <div className="container press-kit-contact">
          <div>
            <p className="press-kit-kicker">Media Contact</p>
            <h2>For press, partnerships, and brand references</h2>
            <p>
              For interviews, media requests, attribution checks, or partnership references, contact the Brew Your Brand team.
            </p>
          </div>
          <a href="mailto:info@brewyourbrand.in">info@brewyourbrand.in</a>
        </div>
      </section>
    </main>
    <BybFooter />
  </>
);

export default PressMediaKitPage;
